import React, { useState } from 'react';
import { MapPin, Mail, Phone, Instagram, Facebook, Youtube, Send } from 'lucide-react';
import { businessInfo } from '../data/mock';
import axios from 'axios';
import useReveal from '../hooks/useReveal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const infoReveal = useReveal();
  const formReveal = useReveal();

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        full_name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        message: formData.message || 'General inquiry',
      });
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error(error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 md:py-36 bg-onyx">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20">
          {/* Info */}
          <div
            ref={infoReveal.ref}
            className={`reveal ${infoReveal.visible ? 'is-visible' : ''}`}
          >
            <p className="eyebrow mb-6">
              <span className="inline-block w-8 h-px bg-silverx align-middle mr-3" />
              Concierge
            </p>
            <h2 className="font-serif text-ivory text-4xl sm:text-5xl lg:text-[56px] leading-[1.06] mb-8">
              Let's begin your{' '}
              <span className="brand-italic chrome-rose">glow-up</span>.
            </h2>
            <p className="text-platinum text-base md:text-lg leading-relaxed mb-12 max-w-lg">
              Share a few details and our concierge team will reach out to curate your
              first Privé consultation.
            </p>

            <div className="space-y-8">
              {[
                { icon: MapPin, label: 'Studio', value: businessInfo.address },
                { icon: Mail, label: 'Email', value: businessInfo.email, href: `mailto:${businessInfo.email}` },
                { icon: Phone, label: 'Phone', value: businessInfo.phone, href: `tel:${businessInfo.phone}` },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-5">
                  <div className="w-11 h-11 border border-pewter flex items-center justify-center flex-shrink-0">
                    <item.icon className="text-blush" size={16} />
                  </div>
                  <div>
                    <p className="eyebrow mb-2">{item.label}</p>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="text-ivory hover:text-blush transition-colors leading-relaxed text-sm md:text-base"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-ivory leading-relaxed text-sm md:text-base">
                        {item.value}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Social */}
            <div className="mt-12 pt-10 border-t border-pewter/60">
              <p className="eyebrow mb-5">Follow Privé</p>
              <div className="flex items-center gap-3">
                {[
                  { Icon: Instagram, url: businessInfo.social.instagram, label: 'Instagram' },
                  { Icon: Facebook, url: businessInfo.social.facebook, label: 'Facebook' },
                  { Icon: Youtube, url: businessInfo.social.youtube, label: 'YouTube' },
                ].map(({ Icon, url, label }) => (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    data-testid={`social-${label.toLowerCase()}`}
                    className="w-11 h-11 border border-pewter flex items-center justify-center text-platinum hover:text-onyx hover:bg-blush hover:border-blush transition-colors"
                  >
                    <Icon size={15} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Form */}
          <div
            ref={formReveal.ref}
            className={`reveal ${formReveal.visible ? 'is-visible' : ''} bg-carbon border border-pewter/70 p-8 md:p-12`}
            style={{ '--reveal-delay': '120ms' }}
          >
            {submitStatus === 'success' ? (
              <div
                className="text-center py-16"
                data-testid="contact-success"
              >
                <div className="w-14 h-14 border border-blush flex items-center justify-center mx-auto mb-6">
                  <Send className="text-blush" size={20} />
                </div>
                <h3 className="font-serif text-ivory text-3xl mb-3">Thank you.</h3>
                <p className="text-silverx leading-relaxed max-w-sm mx-auto">
                  Your inquiry has been received. Our concierge will reach out within
                  24 hours to curate your consultation.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="eyebrow block mb-3">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Jane Smith"
                    data-testid="contact-name"
                    className="w-full bg-transparent border-b border-pewter focus:border-blush text-ivory placeholder-silverx/60 py-3 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-3">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="jane@example.com"
                    data-testid="contact-email"
                    className="w-full bg-transparent border-b border-pewter focus:border-blush text-ivory placeholder-silverx/60 py-3 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-3">Phone (Optional)</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(323) 555 0123"
                    data-testid="contact-phone"
                    className="w-full bg-transparent border-b border-pewter focus:border-blush text-ivory placeholder-silverx/60 py-3 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-3">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Tell us about your goals…"
                    data-testid="contact-message"
                    className="w-full bg-transparent border-b border-pewter focus:border-blush text-ivory placeholder-silverx/60 py-3 outline-none resize-none transition-colors"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit"
                  className="btn-pill w-full mt-4 inline-flex justify-center items-center gap-3 px-8 py-4 rounded-full bg-ivory text-onyx text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-blush disabled:opacity-50"
                >
                  {isSubmitting ? 'Sending…' : 'Request Consultation'}
                </button>

                {submitStatus === 'error' && (
                  <p className="text-red-300 text-xs text-center mt-2">
                    Something went wrong. Please try again or call the studio directly.
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
